<template>
  <div
    :data-kr-id="kr.id"
    class="flex-column kr"
    :class="{ [`kr-type-${type}`]: true, [`view-${viewType}`]: true }"
  >
    <div
      class="kr-wrapper row  flex-grow-1 mx-0"
      :class="{ expanded }"
      @mouseenter="showOptions = true"
    >
      <div
        class="kr-items d-flex flex-grow-1 align-items-start"
        :class="{ 'detailed-view': showDetailsView }"
      >
        <div class="gripper kr-gripper gripper-icon" v-if="draggable"></div>
        <div class="col-auto icons-col d-flex align-items-center align-self-baseline">
          <collapse-caret
            class="flex-shrink-0 collapse-caret"
            :size="23"
            @click="handleExpanderClick"
            :expanded="expanded"
            v-if="canToggleExpansion"
          />
          <div class="kr-circle-icon-wrapper">
            <i class="kr-icon"></i>
          </div>
          <ownership-short
            class="d-flex"
            :entity="kr"
            entity-type="key_result"
            :length="avatarSize"
          ></ownership-short>
        </div>
        <div class="d-flex kr-meta flex-grow-1" :class="{ 'inline-edit': editable }">
          <div class="col d-flex flex-column justify-content-center kr-title-col">
            <div :class="{ 'position-relative': isSubmittingKr }" v-if="editable">
              <input
                type="text"
                class="flex-grow-1 kr-title-input w-100"
                v-model="krTitle"
                @keyup="lastKey = $event.keyCode"
                @keyup.enter="saveKrTitle"
                placeholder="Add a key result, press [enter] to save, [esc] to cancel"
                v-autofocus="editable"
                @blur="
                  () => lastKey !== KEY_CODES.ENTER && lastKey !== KEY_CODES.ESCAPE && saveKrTitle()
                "
                @focus="editable = true"
                data-prevent-blur="true"
                @click.stop
                v-if="editable"
                v-no-capture
              />
              <spinner size="xs" class="inline-edit-loader" v-if="isSubmittingKr" />
            </div>
            <div class="d-flex flex-grow-1 kr-title-wrapper" @click="handleTitleClick" v-else>
              <span
                class="kr-title wrap-hyphenate"
                :title="kr.title"
                :class="{ bold: expanded }"
                :id="'kr-title-' + uuid"
                v-no-capture
              >
                {{ kr.title }}
              </span>
              <entity-popover
                :target="'kr-title-' + uuid"
                :entity-type="ENTITY_TYPE.KEY_RESULT"
                :goalId="goal.id"
                :org-id="orgId"
                v-if="showGoalTitle"
              />
              <kr-actions-icon-list
                :kr="kr"
                :show-comment-badge="viewType === 'list'"
                :show-details-page-navigator="showDetailsPageNavigator"
                :show-keyboard-hints="true"
                @edit="editable = true"
                @comment="onCommentBadgeClick"
                @open="$emit('open', $event)"
              />
            </div>
            <div class="text-prime text-medium archive-meta d-flex" v-if="kr.isArchived">
              Archived on {{ formatDate(kr.archivedAt) }} by
              {{ userById(kr.archiverId).displayName }}
            </div>
            <div
              class="last-checkin-details text-nowrap text-secondary text-small"
              v-else-if="showDetailsView"
              v-no-capture
            >
              <div class="checkin-item" @click="seeCheckin">
                <span class="text">{{ krStatusText(kr.status) }}</span>
              </div>
              <div class="checkin-item" :title="formatDate(kr.lastCheckinAt)" @click="seeCheckin">
                <span class="text">{{ getRelativeDuration(kr.lastCheckinAt) }}</span>
              </div>
              <div class="checkin-item text-truncate" @click="seeCheckin">
                <span class="text">{{ lastCheckInActor }}</span>
              </div>
            </div>
          </div>
          <div
            v-if="showType && goal"
            class="col-auto goal-type text-medium text-truncate text-secondary"
          >
            {{ team.name }}
          </div>
          <div
            class="col-auto kr-checkin-measurement-container d-flex align-items-baseline justify-content-end pr-0"
            @click.stop
          >
            <div class="kr-measurement text-right position-relative d-flex align-items-center">
              <div
                class="checkin-btn-wrapper"
                :data-kr-id="kr.id"
                v-if="!editable && showCheckinButton"
              >
                <b-button
                  ref="checkinBtn"
                  :id="'kr-checkin-' + kr.id"
                  class="checkin-btn"
                  @click.stop="openCheckinModal"
                  pill
                  variant="primary"
                  size="sm"
                  >Check-in
                </b-button>
              </div>
              <checkin-overdue class="checkin-overdue" v-if="isCheckinOverdue" :kr="kr" />
              <kr-award-icon
                class="mr-2"
                :kr="kr"
                @awarded="$emit('awarded')"
                v-else-if="isAwardable"
              />
              <span
                v-if="!kr.outcome || kr.outcome === 'none'"
                class="kr-status-pill sm kr-status variant"
                :data-variant="VARIANT_MAP['status-' + kr.status]"
                data-apply-bg-color
                >{{ krStatusText(kr.status) }}</span
              >
              <div :id="'status-' + kr.id" class="d-flex align-items-center">
                <div
                  class="col-auto pl-0 variant bold"
                  :data-variant="colorVariant"
                  data-apply-color
                  v-if="kr.isClosed"
                >
                  {{ getOutcomeLabel(kr.outcome) }}
                </div>
                <div class="text-nowrap text-small kr-numbers px-2" v-else>
                  <span>{{ displayMeasurement('current') }}</span>
                  <template v-if="kr.measureBy !== 'boolean'">
                    <span class="px-1 text-secondary">/</span>
                    <span class="text-secondary">{{ displayMeasurement('end') }}</span>
                  </template>
                </div>
                <progress-indicator :completed="completedPercent" :variant="colorVariant" />
              </div>
              <kr-details-popover
                :kr="kr"
                :target="'status-' + kr.id"
                v-if="renderSecondLevelItems"
              />
            </div>
          </div>
        </div>
        <div class="options-menu">
          <kr-options
            :kr="kr"
            :source="source"
            :show-keyboard-hints="true"
            ref="krOptions"
            @open="$emit('open', $event)"
            @moved-kr="$emit('moved-kr', $event)"
            @kr-deleted="$emit('kr-deleted')"
            @kr-archived="$emit('kr-archived')"
            @kr-unarchived="$emit('kr-unarchived')"
            @kr-closed="$emit('kr-closed')"
            @kr-reopened="$emit('kr-reopened')"
            @awarded="$emit('awarded')"
            v-if="showOptionsMenu && showOptions && kr.id"
          />
        </div>
      </div>
    </div>
    <b-collapse
      v-model="expanded"
      :class="'initiatives initiatives-' + kr.id"
      v-if="renderSecondLevelItems"
    >
      <div class="spinner-container d-flex justify-content-center" v-if="!initiatives">
        <spinner />
      </div>
      <div
        ref="initiativesList"
        v-if="initiativesCopy && standardFieldsList"
        :key="initiativesContainerKey"
        class="pt-1"
      >
        <initiatives-header
          :org-id="orgId"
          class="mt-2"
          @sort="sortInitiatives"
          @reset="resetSort"
          @filter="filterInitiatives"
          :team-id="teamId"
          :filtered-by="filteredBy"
          :sorted-by="sortedBy"
          :sorted-direction="sortedDirection"
          :fields-to-display="kr.isArchived ? standardFields : fieldsToDisplay"
          :hide-add-custom-field-icon="kr.isArchived"
          :auto-save-column-settings="true"
          :show-field-settings-in-column="true"
          v-if="initiativesCopy.length > 0"
        />
        <div class="d-inline-block min-w-100">
          <div ref="initiatives">
            <initiative-row
              v-for="initiative in openInitiatives"
              :data-id="initiative.id"
              :key="initiative.localId || initiative.id"
              :orgId="orgId"
              :krId="kr.id"
              :goalId="kr.goalId"
              :initiative="initiative"
              :draggable="!sortedBy || sortedBy === 'rank'"
              :initiatives="groupedInitiatives"
              :fields-to-display="kr.isArchived ? standardFields : fieldsToDisplay"
              :selectable="selectable"
              :container-width="containerWidth"
              view-type="child"
              @delete-initiatives="onInitiativeDelete"
              @update-initiative="onInitiativeUpdate"
              @save-initiative="setIsInitiativeAdditionAllowed"
              @change-is-closed="updateClosedInitiativesNavigation"
            ></initiative-row>
          </div>
          <div class="action-link-row-wrapper" v-if="initiatives">
            <div class="action-links">
              <b-button
                variant="link"
                class="text-secondary add-new-btn text-medium action-row-item"
                @click="addNewInitiative"
              >
                Add Initiative
              </b-button>
              <add-a-i-initiatives-link
                class="action-row-item"
                @initiatives-created="refreshInitiatives"
                :kr="kr"
              />
              <b-button
                v-if="closedInitiatives.length > 0"
                variant="link"
                class="text-secondary text-medium action-row-item"
                @click="showClosed = !showClosed"
                >{{ showClosed ? 'Hide Closed' : 'Show Closed' }}
              </b-button>
            </div>
          </div>
        </div>
      </div>
      <!-- refs: initiatives and closedInitiatives are being accessed in GoalViewer -->
      <b-collapse v-model="showClosed" class="note" ref="closedInitiatives">
        <initiative-row
          class="completed"
          v-for="initiative in closedInitiatives"
          :key="initiative.localId || initiative.id"
          :data-id="initiative.id"
          :orgId="orgId"
          :krId="kr.id"
          :goalId="kr.goalId"
          :initiative="initiative"
          :draggable="!sortedBy || sortedBy === 'rank'"
          :fields-to-display="kr.isArchived ? standardFields : fieldsToDisplay"
          :container-width="containerWidth"
          view-type="child"
          @update-initiative="onInitiativeUpdate"
          @delete-initiatives="onInitiativeDelete"
        ></initiative-row>
      </b-collapse>
    </b-collapse>
    <max-initiatives-paywall-modal
      v-model="isPaywallModalShown"
      :org-id="orgId"
      v-if="isPaywallModalShown"
    />
  </div>
</template>
<script>
import KrAwardIcon from '@/components/award/KrAwardIcon'
import CheckinOverdue from '@/components/CheckinOverdue'
import CollapseCaret from '@/components/CollapseCaret'
import EntityPopover from '@/components/EntityPopover'
import AddAIInitiativesLink from '@/components/initiative/aiInitiatives/AddAIInitiativesLink.vue'
import InitiativeRow from '@/components/initiative/InitiativeRow'
import InitiativesHeader from '@/components/initiative/InitiativesHeader.vue'
import KrActionsIconList from '@/components/kr/KrActionsIconList.vue'
import KrActivityModal from '@/components/kr/KrActivityModal'
import KrDetailsPopover from '@/components/KrDetailsPopover'
import KrOptions from '@/components/KrOptions'
import MaxInitiativesPaywallModal from '@/components/paywalls/MaxInitiativesPaywallModal.vue'
import ProgressIndicator from '@/components/ProgressIndicator'
import Spinner from '@/components/Spinner'
import { EVENT_ACTION, EVENT_CATEGORY, EVENT_NAME, SOURCE } from '@/constants/analytics'
import { VARIANT_MAP } from '@/constants/common'
import { ENTITY_TYPE } from '@/constants/entities'
import events from '@/constants/events'
import { featureFlags } from '@/constants/featureFlags'
import { DEFAULT_IMPACT, DEFAULT_STATUS } from '@/constants/initiative'
import { KEY_CODES } from '@/constants/keyCodes'
import { EXT_LOOKUP, KR_MEASURING_UNIT, KR_ROW } from '@/constants/kr'
import { DEFAULT_ERROR } from '@/constants/messages'
import { TOAST_TYPE } from '@/constants/toasts'
import { formatHyphenatedString, krMeasurementText } from '@/goalUtils'
import ComponentIdMixin from '@/mixins/ComponentIdMixin'
import EventBusMixin from '@/mixins/EventBusMixin'
import EventBus from '@/EventBus'
import ExpandCollapseMixin from '@/mixins/ExpandCollapseMixin'
import FeaturesMixin from '@/mixins/FeaturesMixin'
import InitiativeDisplayFieldsMixin from '@/mixins/InitiativeDisplayFieldsMixin'
import InitiativesDragAndDropMixin from '@/mixins/InitiativesDragAndDropMixin'
import InitiativesMixin from '@/mixins/InitiativesMixin'
import MediaQueryMixin from '@/mixins/MediaQueryMixin'
import * as keymapService from '@/services/keymapService/keymapService'
import { calculateCompletionPercent } from '@/utils/calculateCompletionPercent'
import filterInitiatives from '@/utils/filterItems'
import { formatISOStringDate, getRelativeDuration, MILLIS_IN_A_DAY } from '@/utils/formatDate'
import getOutcomeLabel from '@/utils/getOutcomeLabel'
import multiClickHandler from '@/utils/multiClickHandler'
import showAPIError from '@/utils/showAPIError'
import showModal from '@/utils/showModal'
import { BButton, BCollapse } from 'bootstrap-vue'
import * as R from 'ramda'
import { mapActions } from 'vuex'
import { trackEvent } from '../analytics'
import expansionState from '../expansionState'
import OwnershipShort from './OwnershipShort'

/**
 * This component shows KR information in a row. It also handles checkin for the KR
 *
 * Props:
 *  - kr: Model of KR
 *  - orgId
 *  - type
 *
 * Emits:
 *  - kr-deleted
 *  - kr-archived
 *  - kr-unarchived
 *  - kr-closed
 *  - kr-reopened
 *  - moved-kr
 *  - awarded
 *
 *  // TODO: uniform the naming convention of emitted events name. It can be <entity>-<action>. e.g kr-archived
 */
export default {
  name: KR_ROW,
  components: {
    AddAIInitiativesLink,
    KrActionsIconList,
    OwnershipShort,
    BButton,
    BCollapse,
    InitiativeRow,
    Spinner,
    KrDetailsPopover,
    ProgressIndicator,
    InitiativesHeader,
    CheckinOverdue,
    KrOptions,
    KrAwardIcon,
    EntityPopover,
    CollapseCaret,
    MaxInitiativesPaywallModal
  },
  props: {
    kr: Object,
    goalId: Number,
    orgId: Number,
    expand: {
      type: Boolean,
      default: false
    },
    canToggleExpansion: {
      type: Boolean,
      default: true
    },
    showDetailsPageNavigator: {
      type: Boolean,
      default: true
    },
    showOptionsMenu: {
      type: Boolean,
      default: true
    },
    /**
     * this tells if kr is rendering under a goal or child goal
     * acceptable values - goal | child_goal
     */
    type: {
      type: String,
      default: 'goal'
    },
    /**
     * Shows gripper icons if turned on
     */
    draggable: {
      default: true
    },
    /**
     * location from where this component is loaded
     */
    source: String,
    /**
     * size of owner avatar
     */
    avatarSize: {
      default: 20
    },
    /**
     * show name of org/team name
     */
    showType: {
      default: false
    },
    /**
     * show last checkin status, comment and date
     */
    showLastCheckin: {
      default: false
    },
    /**
     * show checkin button
     */
    showCheckinButton: {
      default: true
    },
    /**
     * show kr title in edit mode on load
     */
    editOnLoad: {
      default: false
    },
    viewType: {
      type: String,
      default: ''
    },
    showGoalTitle: Boolean,
    /*
    Whether the can be selected. This cascades to initiatives as well
    */
    selectable: {
      type: Boolean,
      default: true
    }
  },
  mixins: [
    InitiativesDragAndDropMixin,
    InitiativesMixin,
    ExpandCollapseMixin,
    InitiativeDisplayFieldsMixin,
    EventBusMixin,
    MediaQueryMixin,
    ComponentIdMixin,
    FeaturesMixin
  ],
  data() {
    return {
      expanded:
        this.canToggleExpansion && expansionState.exists('kr', this.kr.id)
          ? !!expansionState.get('kr', this.kr.id)
          : this.expand,
      initiatives: null,
      initiativesCopy: null,
      KR_MEASURING_UNIT,
      KEY_CODES,
      EXT_LOOKUP,
      // showClosed is being accessed in GoalViewer
      showClosed: false,
      sortedBy: null,
      sortedDirection: null,
      editable: !this.kr.id || this.editOnLoad,
      krTitle: this.kr.title,
      lastKey: null,
      showOptions: false,
      // render items that are not supposed to be shown upfront. i.e BCollapse, KrDetailsPopover
      renderSecondLevelItems: false,
      filteredBy: {},
      ENTITY_TYPE,
      containerWidth: null,
      isSubmittingKr: false,
      VARIANT_MAP,
      isInitiativeAdditionAllowed: true,
      isPaywallModalShown: false
    }
  },
  computed: {
    goal() {
      return this.$store.getters.goalById(this.goalId)
    },
    completedPercent() {
      const { current, start, end } = this.kr
      return calculateCompletionPercent(start, end, current)
    },
    isCheckinOverdue() {
      return this.kr.isCheckinOverdue && (!this.kr.isClosed && !this.goal.isClosed)
    },
    team() {
      return this.$store.getters.teamById(this.teamId)
    },
    teamId() {
      return this.goal.teamId
    },
    org() {
      return this.$store.getters.orgById(this.kr.organizationId)
    },
    lastCheckInActor() {
      const actorId = R.path(['lastCheckinEvent', 'actorId'], this.kr)
      const actor = this.userById(actorId)
      return actor && actor.firstName
    },
    showDetailsView() {
      return this.showLastCheckin && this.kr.lastCheckinAt
    },
    isAwardable() {
      const progress = (this.kr.current - this.kr.start) / (this.kr.end - this.kr.start)
      let lastCheckinAt = this.kr.lastCheckinAt
      let isOldCheckin = true
      if (lastCheckinAt) {
        lastCheckinAt = new Date(lastCheckinAt)
        isOldCheckin = Date.now() - lastCheckinAt.getTime() > MILLIS_IN_A_DAY * 14
      }
      return progress > 0.25 && !isOldCheckin
    },
    colorVariant() {
      return VARIANT_MAP['outcome-' + this.kr.outcome] || VARIANT_MAP['status-' + this.kr.status]
    }
  },
  methods: {
    ...mapActions(['getInitiatives', 'patchKR', 'getGoal', 'updateGoal']),
    displayMeasurement(measurement) {
      return krMeasurementText(this.kr.measureBy, this.kr[measurement], 'compact', 3)
    },
    krStatusText: formatHyphenatedString,
    async openCheckinModal() {
      if (!this.goal) {
        await this.fetchGoal()
      }
      this.showKrActivityModal(true)
    },
    handleExpanderClick() {
      this.toggleExpansionAndSelect()
    },
    handleTitleClick() {
      multiClickHandler({
        onSingleClick: () => {
          const selectedText = window.getSelection().toString()
          if (!selectedText) {
            this.isDesktopMode ? this.toggleExpansionAndSelect() : this.$emit('open')
          }
        },
        onDoubleClick: () => {
          this.editable = true
          this.krTitle = this.kr.title
        }
      })
    },
    toggleExpansionAndSelect() {
      if (this.canToggleExpansion) {
        this.expanded = !this.expanded
      }
      this.$nextTick(function() {
        if (this.isCurrentKrSelectable()) {
          this.selectCurrentKr()
        }
      })
    },
    /*
    This is used by InitiativesDragAndDropMixin
     */
    updateInitiatives(initiatives) {
      this.resetInitiatives(initiatives)
    },
    showPaywall() {
      this.isPaywallModalShown = true
    },
    addNewInitiative() {
      if (!this.isInitiativeAdditionAllowed) {
        this.showPaywall()
        return
      }
      let newInitiative = {
        id: this.createInitiativeId(),
        title: '',
        impact: DEFAULT_IMPACT,
        coOwnerIds: this.kr.coOwnerIds,
        ownerId: this.kr.ownerId,
        isClosed: false,
        status: DEFAULT_STATUS,
        keyResultId: this.kr.id,
        otype: ENTITY_TYPE.INITIATIVE
      }
      this.resetInitiatives([...(this.initiatives || []), newInitiative])
      if (this.initiatives.length === 1) {
        this.$nextTick(this.enableInitiativeDragAndDrop)
      }
      trackEvent(EVENT_CATEGORY.ACTIVITY, EVENT_ACTION.ADD_INITIATIVE)
    },
    addInitiative() {
      if (!this.expanded) {
        this.expanded = true
      }
      setTimeout(this.addNewInitiative, 200)
    },
    clearSelection() {
      const selectedItem = keymapService.selectedListItem(document)
      if (selectedItem) selectedItem.classList.remove('selected')
    },
    isCurrentKrSelectable() {
      return this.$el.classList.contains('select-list-item')
    },
    selectCurrentKr() {
      this.clearSelection()
      this.$el.classList.add('selected')
    },
    async loadInitiatives() {
      if (!this.standardFieldsList) {
        await this.fetchDisplayFields()
      }
      return this.refreshInitiatives()
    },
    /**
     * This function is getting used in KR.vue to refresh the initiatives on bulk action.
     */
    async refreshInitiatives(additionalParams = {}) {
      let response
      try {
        response = await this.getInitiatives({
          orgId: this.orgId,
          krId: this.kr.id,
          includeCustomFields: true,
          includeStandardFields: this.computedFields.join(','),
          hasParent: false,
          ...additionalParams
        })
      } catch (e) {
        showAPIError(e, undefined, 'Error while fetching initiatives')
        throw e
      }
      const initiatives = response.results
      if (this.initiatives?.length === 1 && this.initiatives[0].id.toString().startsWith('local')) {
        // if some initiative creation is under progress
        this.resetInitiatives([...initiatives, ...this.initiatives])
        this.$nextTick(function() {
          // focus the initiative(input) under creation
          const activeElement = document.activeElement
          activeElement.tagName === 'INPUT' && activeElement.scrollIntoView()
        })
      } else {
        this.resetInitiatives(initiatives)
      }
      return response
    },
    resetInitiatives(initiatives) {
      this.initiatives = initiatives
      this.initiativesCopy = initiatives
    },
    setSortParams(sortedBy, sortedDirection) {
      this.sortedBy = sortedBy
      this.sortedDirection = sortedDirection
    },
    sortInitiatives({ by: sortBy, direction: sortDirection } = {}) {
      const previousSortedBy = this.sortedBy
      const previousSortedDirection = this.sortedDirection
      const orderBy = sortBy ? this.getOrderByFromSortParams({ sortBy, sortDirection }) : null
      this.setSortParams(sortBy, sortDirection)
      this.refreshInitiatives({
        orderBy
      })
        .then(response => {
          const { sortedBy, sortedDirection } = this.getSortParamsFromOrderBy(response.orderBy)
          this.setSortParams(sortedBy, sortedDirection)
        })
        .catch(() => {
          this.setSortParams(previousSortedBy, previousSortedDirection)
        })
    },
    filterInitiatives({ filterParam, filterValue, selected }) {
      let appliedFilters = this.filteredBy[filterParam] || {}
      appliedFilters = { ...appliedFilters, [filterValue]: selected }
      this.filteredBy = { ...this.filteredBy, [filterParam]: appliedFilters }
      this.initiatives = filterInitiatives({
        filteredBy: this.filteredBy,
        items: this.initiativesCopy
      })
    },
    resetSort() {
      this.sortInitiatives({ by: null, direction: null })
    },
    onKrMove(payload) {
      this.$emit('moved-kr', payload)
    },
    cancelInlineEditing() {
      this.lastKey = KEY_CODES.ESCAPE
      this.editable = false
      this.krTitle = this.kr.title
      this.$emit('cancel-edit')
    },
    updateKr() {
      return this.patchKR({
        id: this.kr.id,
        goalId: this.goalId,
        organizationId: this.kr.organizationId,
        title: this.krTitle
      })
    },
    async createKr() {
      const kr = {
        ...this.kr,
        title: this.krTitle
      }
      const keyResults = [...this.goal.keyResults, kr]
      const updatedGoal = R.assoc('keyResults', keyResults, this.goal)
      return this.updateGoal(updatedGoal)
    },
    async saveKrTitle() {
      if (this.isSubmittingKr) return
      try {
        this.isSubmittingKr = true
        if (this.kr.id) {
          await this.updateKr()
          this.editable = false
          EventBus.$emit('RELOAD_KR_EVENTS')
        } else {
          if (this.krTitle) {
            await this.createKr()
            this.$emit('created')
            EventBus.$emit('RELOAD_KR_EVENTS')
          } else {
            this.$emit('cancel-edit')
          }
          this.editable = false
        }
      } catch (e) {
        let message =
          R.path(['response', 'data', 'detail', 'title', '0'], e) ||
          R.pathOr(DEFAULT_ERROR, ['response', 'data', 'message'], e)
        this.eventBusEmit('ADD_TOAST', { message, type: TOAST_TYPE.ERROR })
      } finally {
        this.isSubmittingKr = false
      }
    },
    fetchGoal() {
      return this.getGoal({
        orgId: this.kr.organizationId,
        goalId: this.goalId
      })
    },
    async onCommentBadgeClick() {
      if (!this.goal) {
        await this.fetchGoal()
      }
      trackEvent(EVENT_CATEGORY.NAVIGATION, EVENT_ACTION.ACTIVITY_MODAL, EVENT_NAME.KR)
      this.showKrActivityModal(false)
    },
    showKrActivityModal(showCheckin) {
      showModal({
        component: KrActivityModal,
        id: `${ENTITY_TYPE.KEY_RESULT}-${this.kr.id}`,
        props: {
          show: true,
          krId: this.kr.id,
          showCheckin: showCheckin,
          source: this.source === SOURCE.KR_PAGE ? SOURCE.KR_PAGE_BUTTON : this.source
        },
        on: {}
      })
    },
    showActivityModal() {
      this.openCheckinModal()
    },
    formatDate: formatISOStringDate,
    getRelativeDuration,
    userById(id) {
      return this.$store.getters.userById(id) || {}
    },
    async seeCheckin() {
      if (!this.goal) {
        await this.fetchGoal()
      }
      trackEvent(EVENT_CATEGORY.NAVIGATION, EVENT_ACTION.SEE_CHECKIN)
      this.showKrActivityModal(false)
    },
    /*
    this is getting used on KR page
     */
    showAwardModal() {
      this.showOptions = true
      this.$nextTick(() => {
        this.$refs.krOptions.showAwardModal()
      })
    },
    handleExpansionState() {
      if (this.selectable) {
        this.$nextTick(function() {
          this.updateExpansionMiddleware(this.expanded, '.initiative-row', this.$refs.initiatives)
          this.updateExpansionMiddleware(
            this.expanded && this.showClosed,
            '.initiative-row',
            this.$refs.closedInitiatives.$el
          )
        })
      }
    },
    updateExpansionMiddleware(value, itemSelector, container) {
      if (this.selectable) {
        this.updateExpansion(value, itemSelector, container)
      }
    },
    calculateContainerWidth() {
      this.containerWidth = this.$refs.initiativesList?.getBoundingClientRect().width
    },
    onResize() {
      this.calculateContainerWidth()
    },
    getOutcomeLabel,
    getInitiativesFetchParams() {
      const computedFields = this.computedFields.join(',')
      return {
        includeCustomFields: true,
        includeComputedFields: computedFields,
        hasParent: false
      }
    },
    onInitiativeUpdate(updatedInitiative) {
      const fetchParams = this.getInitiativesFetchParams()
      this.handleInitiativeUpdate(updatedInitiative, fetchParams)
    },
    onInitiativeDelete(initiatives) {
      this.deleteInitiatives(initiatives)
      !this.isInitiativeAdditionAllowed && this.setIsInitiativeAdditionAllowed()
    },
    async setIsInitiativeAdditionAllowed() {
      this.isInitiativeAdditionAllowed = await this.satisfyFeature(
        this.orgId,
        featureFlags.MAX_INITIATIVE_COUNT
      )
    },
    updateClosedInitiativesNavigation() {
      setTimeout(() => {
        this.updateExpansionMiddleware(
          this.showClosed,
          '.initiative-row',
          this.$refs.closedInitiatives.$el
        )
      }, 100)
    }
  },
  async mounted() {
    if (this.editable && this.selectable) {
      this.selectCurrentKr()
    }
    if (this.expanded && !this.initiatives) {
      this.setIsInitiativeAdditionAllowed()
      this.loadInitiatives().then(() => {
        this.$nextTick(function() {
          setTimeout(() => {
            // adding setTimeout since this.$refs.closedInitiatives in not available immediately
            this.enableInitiativeDragAndDrop()
            this.updateExpansionMiddleware(
              false,
              '.initiative-row',
              this.$refs.closedInitiatives?.$el
            )
          }, 1000)
        })
      })
    }
    this.listenToEventBus(events.INITIATIVES_MOVED, payload => {
      const fromKRs = payload.map(item => item[0]).map(i => i.keyResultId)
      const toKRs = payload.map(item => item[1]).map(i => i.keyResultId)
      if ([...toKRs, ...fromKRs].includes(this.kr.id) && this.initiatives) {
        this.loadInitiatives()
      }
    })
    setTimeout(() => (this.renderSecondLevelItems = true), 500)
    this.listenToEventBus('CF_TOGGLED', ({ teamId, organizationId }) => {
      if (this.expanded && teamId === this.teamId && organizationId === this.kr.organizationId) {
        this.refreshInitiatives()
      }
    })
    this.listenToEventBus(events.INITIATIVES_DELETED, this.onInitiativeDelete)
  },
  updated() {
    if (!this.containerWidth && this.$refs.initiativesList) {
      this.calculateContainerWidth()
      window.addEventListener('resize', this.onResize)
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
  },
  watch: {
    async expanded(isExpanded) {
      expansionState.set('kr', this.kr.id, isExpanded)
      if (isExpanded) {
        if (!this.initiatives) {
          this.setIsInitiativeAdditionAllowed()
          await this.loadInitiatives()
        }
        this.$nextTick(function() {
          this.enableInitiativeDragAndDrop()
        })
      }
      this.handleExpansionState()
    },
    showClosed() {
      this.updateClosedInitiativesNavigation()
    },
    editable() {
      if (this.editable) {
        this.selectable && this.$nextTick(this.selectCurrentKr)
        this.krTitle = this.kr.title
        this.lastKey = null
      }
    }
  }
}
</script>
<style lang="scss" scoped>
@import '~@/styles/styleConfigurations';
@import '~@/styles/components/kr';
@import '~@/styles/goals';
@import '~@/styles/drag';
@import '~@/styles/components/badge';
@import '~@/styles/components/input';

$grid-gutter-width: 6px;
$corner-radius: 5px;

.row {
  margin-left: -$grid-gutter-width;
  margin-right: -$grid-gutter-width;
}

.col,
.col-auto {
  padding-left: $grid-gutter-width;
  padding-right: $grid-gutter-width;
}

.kr:first-of-type {
  margin-top: 10px;
}

.collapse-caret {
  margin-right: 6px;
}

.kr.kr-type-child_goal {
  padding-right: 0;
}

.kr {
  font-size: 0.875rem;
  padding: 0 20px 0 10px;

  &:not(:last-child) {
    margin-bottom: 5px;
  }

  .kr-wrapper {
    position: relative;

    &.expanded,
    &:hover {
      .checkin-btn-wrapper {
        opacity: 1;
      }

      .gripper {
        display: initial;
      }

      .collapse-caret {
        opacity: 1;
      }
    }
  }

  .kr-items {
    padding: 5px 0 5px 6px;
    min-width: 0;
  }

  &.selected {
    .kr-items {
      border-radius: $corner-radius;
      background-color: $selectionColor;
      box-shadow: 0 1px 1px rgba(168, 168, 168, 0.25);
    }

    .checkin-btn-wrapper {
      background-color: transparent;
    }

    .collapse-caret {
      opacity: 1;
    }
  }

  .initiatives {
    @include scrollbarWidth($scrollSecondary);

    margin-left: 45px;
    padding-bottom: 20px;
    overflow-x: auto;
  }
}

.add-new-btn {
  padding-left: 6px;
}

.kr:last-of-type {
  margin-bottom: 0px;
}

.kr-title-col {
  cursor: pointer;
  line-height: 1.4;
  min-width: 0;
  padding-left: 3px;
}

.inline-edit .kr-title-col {
  padding-top: 0;
  padding-bottom: 0;
  padding-left: 0;
}

.inline-edit input {
  padding: 1px 3px;
  font-size: 0.9375rem;
}

.inline-edit .last-checkin-details {
  padding-left: 3px;
}

.kr-title {
  font-size: 0.9375rem;
  -webkit-line-clamp: 2;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  padding-top: 2px;
}

.kr-checkin-measurement-container {
  margin-right: 8px;
}

.kr-checkin-measurement-container .kr-checkin-container {
  display: none;
}

.kr-checkin-measurement-container .kr-measurement {
  display: block;
}

.kr-checkin-container button {
  font-size: 12px;
  padding-top: ($spacer * 0.1);
  padding-bottom: ($spacer * 0.1);
}

.kr-measurement {
  font-size: 14px;
  margin-top: 4px;
}

.checkin-btn-wrapper {
  opacity: 0;
  margin-top: -4px;
  padding-right: 6px;
  border-radius: 25px;
  background-color: white;

  /deep/ button {
    font-size: 11px;
    padding: 0.1rem 0.5rem;
  }
}

.icons-col {
  padding: 0 3px 0 6px;
}

.gripper {
  display: none;
  position: absolute;
  left: 6px;
  top: 3px;
  cursor: move;
}

.spinner-container {
  padding: 10px;
}

.action-link-row-wrapper {
  padding-left: 54px;
  font-size: 0.75rem;
  padding-top: 8px;
  padding-bottom: 8px;
}

.action-links {
  @include sticky(false, 54px);
  display: inline-flex;
}

/deep/ .initiative-row:last-child .cell {
  border-bottom: 1px solid #edeff1;
}

/deep/ .initiative-row.selected:last-child .cell {
  border-color: transparent;
}

/deep/ .initiative-row .summary-wrapper {
  padding-right: 0;
}

.kr-circle-icon-wrapper {
  margin-right: 6px;
}

.kr-status-pill {
  display: none;
}

.kr-title-col:hover {
  /deep/ .expander-icon,
  /deep/ .edit-icon,
  /deep/ .comment-badge-icon {
    opacity: 1;
  }
}

/deep/ .initiatives-head .row-wrapper .row {
  padding-right: 0;
}

.kr-wrapper /deep/ .edit-menu-button[aria-expanded='false'] {
  display: none;
}

.kr-wrapper:hover /deep/ .edit-menu-button {
  display: block;
}

.goal-type {
  max-width: 200px;
  margin-top: 4px;
}

.last-checkin-details {
  display: flex;
  align-items: center;
  padding-top: 4px;
}

.checkin-item:hover .text {
  filter: brightness(70%);
}

.checkin-item:not(:last-child) {
  position: relative;
  margin-right: 18px;

  &:after {
    content: ' ';
    height: 4px;
    width: 4px;
    background-color: $secondary;
    border-radius: 50%;
    position: absolute;
    right: -11px;
    top: 50%;
    transform: translateY(-50%);
  }
}

.kr-meta {
  min-width: 0;
  height: 100%;
}

.view-detail {
  .kr-meta {
    align-self: center;

    &:not(.inline-edit) {
      margin-top: 4px;
    }
  }

  .kr-measurement {
    margin-top: 4px;
  }

  .icons-list {
    margin-top: 8px;
  }

  .checkin-btn-wrapper {
    padding: 6px;
  }

  /deep/ .edit-menu {
    padding-top: 4px;
  }

  .kr-title {
    padding-top: 4px;
  }

  .kr-checkin-measurement-container {
    min-width: 210px;
  }

  .options-menu {
    margin-top: 4px;
  }

  .kr-wrapper .kr-title,
  .kr-title-col input {
    font-size: 21px;
    font-weight: 600;
  }

  .kr .gripper {
    display: none !important;
  }

  .inline-edit-loader {
    top: 7px;
  }
}

.options-menu {
  width: 36px;
  flex-shrink: 0;
  margin-top: 2px;
}

.kr-status {
  width: 70px;
  text-transform: initial;
}

@include media-breakpoint-down(md) {
  .kr.selected .kr-items {
    background-color: inherit;
    box-shadow: none;
  }
  .kr-meta {
    flex-direction: column;

    .col {
      flex-basis: auto;
    }
  }
  .kr-checkin-measurement-container {
    width: initial;
    justify-content: flex-start !important;
    margin-right: 0;
    margin-left: 6px;
  }
  .kr-measurement {
    padding-bottom: 12px;
  }
  .checkin-btn-wrapper {
    display: none !important;
  }
  .kr-checkin-measurement-container .kr-measurement {
    display: flex;
    align-items: center;
  }
  .kr-status-pill {
    display: initial;
    margin-right: 16px;
    text-align: center;
  }
  .progressor-wrapper {
    display: none;
  }
  .completed-percent {
    font-size: 1rem;
  }
  .kr-checkin-measurement-container {
    border-bottom: 1px solid $borderSecondary;
  }
  .kr {
    margin-bottom: 8px;
  }
  .goal-type {
    display: none;
  }
  .icons-list,
  .checkin-overdue,
  .options-menu {
    display: none;
  }
  .last-checkin-details {
    display: none;
  }
  .action-link-row-wrapper {
    padding-left: 0px;
  }
  .action-links {
    left: 0;
  }
}
</style>

<style lang="scss">
@import '~@/styles/styleConfigurations';

.kr.gu-mirror .initiative-row .gripper {
  display: none !important;
}

.checkin-popover {
  box-shadow: 0px 1px 7px rgba(62, 62, 62, 0.25);
  border: none;
  max-width: 390px;
}

.checkin-popover {
  .arrow {
    display: none;
  }
}

.checkin-overdue {
  margin-top: -2px;
}

.inline-edit-loader {
  position: absolute;
  right: 8px;
  top: 4px;
}

@include media-breakpoint-down(md) {
  .kr-value-popover {
    display: none;
  }
  .checkin-status-select {
    justify-content: center;
  }
}
</style>
